import React, { useState, useEffect, useRef, useContext } from "react";
import DOMPurify from "dompurify";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import "../css/editor.scss";
import "../css/listOfSlides.scss";
import "../css/EditorWrapper.scss";
import ValueContext from "../utils/contextAPI/contextAPI";
import {
  geteDetailByID,
  createNewPageUpdate,
  updatSlideData,
  ExportDeckToMail,
  getSlideDetailsById,
  deleteSlidesRequest,
  updateDeckInfo,
  validateDeckName,
  PublishHTMLAndPDF,
  postBinder,
  getDeckTraceabilityMatrix,
  PublishHTMLAndPDFBinder,
  postAddCutomScripts,
} from "../services/apis";
import { ReactComponent as DashboardImage } from "../images/home-icon.svg";
import SaveChangesNewIcon from "../images/save-change-image.svg";
import EditIcon from "../images/edit-icon.svg";
import EditIconActive from "../images/EditIconActiveNew.svg";
import SlidsIcon from "../images/slids.svg";
import SlidsIconActive from "../images/slidsiconActive.svg";
import EraseIcon from "../images/eraser-icon.svg";
import CursorEraserIcon from "../images/cursor-eraser-icon.svg";
import EraseIconActive from "../images/EraseIconActive.svg";
import ZoomInNewIcon from "../images/Zoom-In-New-Icon.svg";
import ZoomOutNewIcon from "../images/Zoom-Out-New-Icon.svg";
import PreviewNewIcon from "../images/Preview-New-Icon.svg";
import NewSlideLeftArrowIcon from "../images/new-slide-left-icon.svg";
import NewSlideRightArrowIcon from "../images/new-slide-right-icon.svg";
import FlowCreatorIcon from "../images/flowCreatorIcon.svg";
import DragIndicatorIcon from "../images/drag-indicator-pdf-flow.svg";
import EditIconSimple from "../images/edit.svg";
import DeleteIconOne from "../images/delete-1.svg";
import ModuleStepper from "../components/ModuleStepper/ModuleStepper";
import CustomTooltip from "../components/Tooltip/Tooltip";
import {
  showSuccessToast,
  showErrorToast,
} from "../components/toastComponent/ToastComponent";
import { thirdPartyPlugins, script } from "../utils/editorUtils/editorUtils";
import PreviewModal from "../components/Preview/Preview";
import SlideSwapperLeftPanel from "../components/SlideSwapperLeftPanel";
import SlideDeleteValidator from "../components/Slide/SlideDeleteValidator";
import CircularProgressBar from "../utils/CircularProgressBar";
import "../css/PdfEditor.scss";
import TextField from "@mui/material/TextField";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import "../css/templateForm.scss";
import { convertInDate } from "../utils";

// System-generated name function
const generateDeckName = (baseName = "pdf_eda") => {
  // Sanitize input
  const sanitizedBase =
    baseName
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "")
      .substring(0, 40) || "pdf_eda";

  // Short random suffix (UI-side only)
  const suffix = Math.random().toString(36).substring(2, 8);

  return `${sanitizedBase}_${suffix}`;
};

const HomeIcon = () => <DashboardImage width="20" height="20" />;

const PdfEditor = () => {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const appData = useContext(ValueContext);

  const [deckData, setDeckData] = useState(null);
  const [slides, setSlides] = useState([]);
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);

  const [loading, setLoading] = useState(true);
  const [toolMode, setToolMode] = useState(null); // null, 'select' or 'erase'
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);
  const [slideHtml, setSlideHtml] = useState("");
  const [slideCss, setSlideCss] = useState("");
  // const [iframeSrc, setIframeSrc] = useState(""); // Removed
  const [slideLoading, setSlideLoading] = useState(false);
  const [scalePercent, setScalePercent] = useState(100);
  const [currentStep] = useState(3);
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true);
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreView] = useState(false);
  const [previewUrlReady, setPreviewUrlReady] = useState(false);
  const [selectedAreaForLink, setSelectedAreaForLink] = useState(null);
  const [linkType, setLinkType] = useState("slide"); // 'slide' or 'url'
  const [linkUrl, setLinkUrl] = useState("");
  const [linkSlideId, setLinkSlideId] = useState("");
  const [showAllSlides, setShowAllSlides] = useState(false);
  const [flowCreatorLoading, setFlowCreatorLoading] = useState(false);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [deckLoading, setDeckLoading] = useState(false);
  const [deletePrompt, setDeletePrompt] = useState(false);
  const [slideToDelete, setSlideToDelete] = useState(null);
  const [deleteSlideIndex, setDeleteSlideIndex] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [editingSlideIndex, setEditingSlideIndex] = useState(null);
  const [editingField, setEditingField] = useState(null); // 'name' or 'key_message'
  const [tempSlideDetails, setTempSlideDetails] = useState({});
  const [isDrawing, setIsDrawing] = useState(false); // State to track drawing for overlay pointerEvents
  const [contentHeight, setContentHeight] = useState(768); // Track content height for overlay
  const [contentScale, setContentScale] = useState(1); // Dynamic scale for content based on container

  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // State for custom dropdown
  const dropdownRef = useRef(null); // Ref for custom dropdown

  // Metadata modal state
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [metadataName, setMetadataName] = useState("");
  const [existingMetadataName, setExistingMetadataName] = useState("");
  const [metadataHelperText, setMetadataHelperText] = useState("");
  const [metadataErrorMsg, setMetadataErrorMsg] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [userName, setUserName] = useState("");
  const [traceabilityData, setTraceabilityData] = useState(null);
  const [publishPending, setPublishPending] = useState(false);

  const steps = [
    "Search in PromoMats",
    "Preview & Convert to HTML",
    "Publish HTML",
  ];

  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const htmlWrapperRef = useRef(null);
  const htmlContentRef = useRef(null);
  const isDrawingRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });
  const baseHtmlRef = useRef(null);
  const selectionRef = useRef(null);
  const selectedAreasRef = useRef([]);
  // const iframeRef = useRef(null); // Removed

  const fetchDeckData = async () => {
    setLoading(true);
    setDeckLoading(true);
    geteDetailByID(deckId, (response, err) => {
      setDeckLoading(false);
      if (err) {
        console.error("Error fetching deck data:", err);
        setLoading(false);
      } else {
        const data = response.data;
        setDeckData(data);
        setSlides(data.slides || []);
        // Set appData.deckId for PreviewModal to work correctly
        if (appData?.setDeckId) {
          appData.setDeckId(data);
        }
        if (data.slides?.length > 0) {
          const firstSlide = data.slides[0];
          setSelectedSlide(firstSlide);
          setSelectedSlideIndex(0);
          setImageUrl(
            firstSlide?.slideid?.thumbnailNew || firstSlide?.slideid?.thumbnail
          );
          // Load HTML/CSS for initial slide
          if (firstSlide?.slideid?._id) {
            loadSlideContent(firstSlide.slideid._id);
          }
        }
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    if (deckId) {
      fetchDeckData();
    } else if (location.state?.deckData) {
      const data = location.state.deckData;
      setDeckData(data);
      setSlides(data.slides || []);
      // Set appData.deckId for PreviewModal to work correctly
      if (appData?.setDeckId) {
        appData.setDeckId(data);
      }
      if (data.slides?.length > 0) {
        const firstSlide = data.slides[0];
        setSelectedSlide(firstSlide);
        setSelectedSlideIndex(0);
        setImageUrl(
          firstSlide?.slideid?.thumbnailNew || firstSlide?.slideid?.thumbnail
        );
        // Load HTML/CSS for initial slide
        if (firstSlide?.slideid?._id) {
          loadSlideContent(firstSlide.slideid._id);
        }
      }
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckId]);

  // Initialize userName and metadata name
  useEffect(() => {
    const nameObj = localStorage.getItem("currentUserName");
    setUserName(nameObj || "");

    // Initialize metadata name from deckData or use system-generated name
    if (deckData?.name) {
      setMetadataName(deckData.name);
      setExistingMetadataName(deckData.name);
    } else {
      const generatedDeckName = generateDeckName("pdf_eda");
      setMetadataName(generatedDeckName);
      setExistingMetadataName(generatedDeckName);
    }
  }, [deckData]);

  // Handle click outside to close custom dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update content height for overlay when content changes
  useEffect(() => {
    const updateContentHeight = () => {
      if (htmlContentRef.current) {
        let height = Math.max(
          htmlContentRef.current.scrollHeight,
          htmlContentRef.current.offsetHeight,
          768
        );
        height = htmlWrapperRef.current?.children?.[2]?.children?.[0]?.children?.[0]?.height > 768 ? height : 768;
        setContentHeight(height);
      }
    };

    updateContentHeight();

    // Use ResizeObserver to track content size changes
    if (htmlContentRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        updateContentHeight();
      });
      resizeObserver.observe(htmlContentRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [slideHtml, selectedSlideIndex, selectedSlide]);

  // Calculate dynamic scale for content based on container dimensions
  useEffect(() => {
    const calculateContentScale = () => {
      if (htmlWrapperRef.current) {
        const container = htmlWrapperRef.current.parentElement; // pdf-editor-canvas-container
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const contentMaxWidth = 1024; // Max content width
          const padding = 32; // Account for margins/padding
          const availableWidth = containerRect.width - padding;
          const contentMaxHeight = 768; // Max content height
          const availableHeight = containerRect.height - padding;

          // Calculate scale: available width and height / content width and height, but don't scale up beyond 1
          const calculatedWidthScale = Math.min(availableWidth / contentMaxWidth, 1);
          const calculatedHeightScale = Math.min(availableHeight / contentMaxHeight, 1);

          // Apply the zoom scalePercent as well based on less than 768px height and width.
          const calculatedScale = contentHeight <= 768 && showRightPanel && isLeftPanelOpen ? Math.min(calculatedHeightScale, calculatedWidthScale) : calculatedWidthScale;
          const finalScale = calculatedScale * (scalePercent / 100);
          setContentScale(finalScale);
        }
      }
    };

    calculateContentScale();

    // Recalculate on window resize
    const handleResize = () => {
      calculateContentScale();
    };

    window.addEventListener("resize", handleResize);

    // Use ResizeObserver to track container size changes
    if (htmlWrapperRef.current?.parentElement) {
      const resizeObserver = new ResizeObserver(() => {
        calculateContentScale();
      });
      resizeObserver.observe(htmlWrapperRef.current.parentElement);

      return () => {
        window.removeEventListener("resize", handleResize);
        resizeObserver.disconnect();
      };
    }

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [scalePercent, slideHtml, selectedSlideIndex, isLeftPanelOpen, showRightPanel, contentHeight]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(slides);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === selectedSlideIndex) {
      setSelectedSlideIndex(destinationIndex);
    } else if (
      sourceIndex < selectedSlideIndex &&
      destinationIndex >= selectedSlideIndex
    ) {
      setSelectedSlideIndex((prev) => prev - 1);
    } else if (
      sourceIndex > selectedSlideIndex &&
      destinationIndex <= selectedSlideIndex
    ) {
      setSelectedSlideIndex((prev) => prev + 1);
    }

    // Prepare payload for API
    const reorderedSlides = items.map((item, index) => ({
      index: index + 1,
      slideid: item?.slideid?._id || item?.slideid,
      children: item.children || [],
    }));

    const reorderedSlideItems = items.map((item, i) => {
      item.index = i + 1;
      return item;
    });

    // Call API to update slide order
    if (deckId || deckData?._id) {
      const deckIdToUse = deckId || deckData?._id;
      setReorderLoading(true);
      createNewPageUpdate(
        deckIdToUse,
        { slides: reorderedSlides },
        (response, err, errorCode) => {
          setReorderLoading(false);
          if (err) {
            console.error("Error reordering slides:", err);
            // Revert to original order on error
            fetchDeckData();
          } else {
            // Update local state on success
            setSlides(reorderedSlideItems);
            // Update deckData if response contains updated data
            if (response?.data) {
              setDeckData((prev) => ({
                ...prev,
                slides: reorderedSlideItems,
              }));
            }
          }
        }
      );
    } else {
      // If no deckId, just update local state
      setSlides(reorderedSlideItems);
    }
  };

  // Function to remove embedded selected areas from HTML
  const removeEmbeddedAreasFromHtml = (html) => {
    if (!html) return html;

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Remove all embedded pdf-selected-area-link elements
    const embeddedAreas = tempDiv.querySelectorAll(".pdf-selected-area-link");
    embeddedAreas.forEach((area) => area.remove());

    // Also remove elements with ccid starting with "pdf-link-"
    const pdfLinks = tempDiv.querySelectorAll('[ccid^="pdf-link-"]');
    pdfLinks.forEach((link) => link.remove());

    return tempDiv.innerHTML;
  };

  // Load slide HTML/CSS content
  const loadSlideContent = (slideId) => {
    if (!slideId) return;

    setSlideLoading(true);
    getSlideDetailsById(slideId, (response, err) => {
      setSlideLoading(false);
      if (!err && response?.data) {
        const slideData = response.data;

        // Get HTML and CSS from content or direct properties
        let html = slideData?.content?.html || slideData?.html || "";
        const css = slideData?.content?.css || slideData?.css || "";

        // Store base HTML (without embedded areas) in ref
        const baseHtml = removeEmbeddedAreasFromHtml(html);
        baseHtmlRef.current = baseHtml;

        // Load saved selected areas for this slide (check both locations: root and content)
        const savedAreas =
          slideData?.selectedAreas || slideData?.content?.selectedAreas;
        if (savedAreas && savedAreas.length > 0) {
          // Merge with existing areas to avoid losing any unsaved areas
          setSelectedAreas((prevAreas) => {
            const existingPositions = new Set();
            const merged = [];

            // First, add all existing areas (unsaved ones that user might have added)
            prevAreas.forEach((area) => {
              const posKey = `${Math.round(area.x)}_${Math.round(
                area.y
              )}_${Math.round(area.width)}_${Math.round(area.height)}`;
              if (!existingPositions.has(posKey)) {
                merged.push(area);
                existingPositions.add(posKey);
              }
            });

            // Then, add saved areas that don't already exist
            savedAreas.forEach((savedArea) => {
              const posKey = `${Math.round(savedArea.x)}_${Math.round(
                savedArea.y
              )}_${Math.round(savedArea.width)}_${Math.round(
                savedArea.height
              )}`;
              if (!existingPositions.has(posKey)) {
                merged.push({
                  id: `area-${Date.now()}-${merged.length}`,
                  x: savedArea.x,
                  y: savedArea.y,
                  width: savedArea.width,
                  height: savedArea.height,
                  linkType: savedArea.linkType || null,
                  linkUrl: savedArea.linkUrl || null,
                  linkSlideId: savedArea.linkSlideId || null,
                });
                existingPositions.add(posKey);
              }
            });

            return merged;
          });

          // After state update, get the merged areas and update HTML
          // Use a small timeout to ensure state has updated
          setTimeout(() => {
            setSelectedAreas((currentAreas) => {
              const updatedHtml = modifyHtmlWithSelectedAreas(
                baseHtml,
                currentAreas
              );
              setSlideHtml(updatedHtml);
              return currentAreas;
            });
          }, 0);

          // Set initial HTML with saved areas for immediate display
          const restoredAreas = savedAreas.map((area, idx) => ({
            id: `area-${Date.now()}-${idx}`,
            x: area.x,
            y: area.y,
            width: area.width,
            height: area.height,
            linkType: area.linkType || null,
            linkUrl: area.linkUrl || null,
            linkSlideId: area.linkSlideId || null,
          }));
          html = modifyHtmlWithSelectedAreas(baseHtml, restoredAreas);
        } else {
          // No saved areas - preserve existing ones if any
          setSelectedAreas((prevAreas) =>
            prevAreas.length > 0 ? prevAreas : []
          );
          // Use base HTML (already cleaned)
          html = baseHtml;
        }

        setSlideHtml(html);
        setSlideCss(css);

        // Create initial iframe src - REMOVED
        /*
        const fullHtml = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <style>
              body {
                margin: 0;
                padding: 0;
                width: 1024px;
                max-width: 1024px;
                overflow-x: hidden;
              }
              * {
                max-width: 1024px;
                box-sizing: border-box;
              }
              img {
                max-width: 1024px;
                width: auto;
                height: auto;
                display: block;
              }
              table {
                max-width: 1024px;
                width: 100%;
                table-layout: auto;
                word-wrap: break-word;
              }
              ${css}
              .pdf-selected-area-link {
                border: 1px dashed #54565B !important;
                border-style: dashed !important;
                border-width: 1px !important;
                border-color: #54565B !important;
                background-color: rgba(84, 86, 91, 0.05) !important;
                cursor: pointer !important;
                box-sizing: border-box !important;
                display: block !important;
                text-decoration: none !important;
              }
              .pdf-selected-area-link:hover {
                background-color: rgba(84, 86, 91, 0.1) !important;
              }
              [data-slide-id] {
                cursor: pointer !important;
              }
            </style>
          </head>
          <body>
            ${html}
          </body>
          </html>
        `;
        */

        // Removed iframe blob creation - using div instead
      } else {
        setSlideHtml("");
        setSlideCss("");
        setSelectedAreas([]);
      }
    });
  };

  const handleSlideClick = (slide, index) => {
    if (selectedSlide.slideid._id === slide.slideid._id) {
      return;
    }
    setLinkSlideId("");
    setLinkUrl("");
    setSelectedAreaForLink(null);
    setSelectedSlide(slide);
    setSelectedSlideIndex(index);
    setImageUrl(slide?.slideid?.thumbnailNew || slide?.slideid?.thumbnail);
    setSelectedAreas([]); // Clear selections when switching slides

    // Load HTML/CSS and selected areas for this slide
    if (slide?.slideid?._id) {
      loadSlideContent(slide.slideid._id);
    }
  };

  // Handle inline editing - show input field
  const handleInlineEdit = (slide, index, field, e) => {
    e.stopPropagation();
    setEditingSlideIndex(index);
    setEditingField(field);
    setTempSlideDetails({
      name: slide?.slideid?.name || `Slide ${index + 1}`,
      key_message: slide?.slideid?.key_message || "Enter Key Message",
    });
  };

  // Update slide details (same as Editor.js)
  const updateSlideDetails = (slideId, name, value, index) => {
    let payLoad = { [name]: value };
    updatSlideData(slideId, payLoad, (response, err, errorCode) => {
      if (err) {
        // Revert to original value on error
        const slide = slides[index];
        setTempSlideDetails({
          name: slide?.slideid?.name || `Slide ${index + 1}`,
          key_message: slide?.slideid?.key_message || "Enter Key Message",
        });
        if (errorCode === 400)
          showErrorToast(err.response?.data?.err?.message || "Update failed");
      } else {
        // Update local state on success
        let tempSlides = [...slides];
        if (tempSlides[index]?.slideid) {
          tempSlides[index].slideid[name] = value;
          setSlides(tempSlides);

          // Update deckData if it exists
          if (deckData) {
            setDeckData((prev) => {
              const updatedSlides = [...(prev.slides || [])];
              if (updatedSlides[index]?.slideid) {
                updatedSlides[index].slideid[name] = value;
              }
              return { ...prev, slides: updatedSlides };
            });
          }
        }
        showSuccessToast("Slide updated successfully");
      }
    });
  };

  // Handle slide message submit (same as Editor.js)
  const slideMessageSubmit = (e, submitType, defaultVal, slideId, index) => {
    if (e.target.value.length > 50) {
      showErrorToast(`${defaultVal} name exceeded the maximum length`);
      return;
    }

    let { name, value } = e.target;

    if (name === "name") {
      const specialChars = /[`!@#$%^&*+=\[\]{};':"\\|,.<>/?~]/;
      if (specialChars.test(value) || value.includes(" ")) return;
    } else if (name === "key_message") {
      const specialChars = /[`!@#$%^&*+=\[\]{};':"\\|,.<>/?~]/;
      if (
        specialChars.test(value) ||
        (value.length > 0 && value[0].includes(" "))
      )
        return;
    }
    if (submitType === "blur" || e.key === "Enter") {
      if (value === "" || value.trim().length === 0)
        value = `Enter${defaultVal}`;
      updateSlideDetails(slideId, name, value, index);
      setEditingSlideIndex(null);
      setEditingField(null);
    }
    setTempSlideDetails({ ...tempSlideDetails, [name]: value });
  };

  // Get field value for display
  const getFieldValue = (slide, index, field) => {
    if (editingSlideIndex === index && editingField === field) {
      return tempSlideDetails[field] || "";
    }
    if (field === "name") {
      return slide?.slideid?.name || `Slide ${index + 1}`;
    }
    return slide?.slideid?.key_message || "Enter Key Message";
  };

  // Handle edit slide - navigate to main editor
  const handleEditSlide = (slide, e) => {
    e.stopPropagation();
    if (slide?.slideid?._id) {
      navigate(`/editor/${slide.slideid._id}`);
    }
  };

  // Handle delete slide - show confirmation
  const handleDeleteSlide = (slide, index, e) => {
    e.stopPropagation();
    if (slides.length <= 1) {
      showErrorToast("Cannot delete the last slide");
      return;
    }
    setSlideToDelete(slide);
    setDeleteSlideIndex(index);
    setDeletePrompt(true);
  };

  // Confirm and execute delete
  const confirmDeleteSlide = () => {
    if (!slideToDelete || deleteSlideIndex === null) return;

    const slideId = slideToDelete?.slideid?._id || slideToDelete?.slideid;
    if (!slideId || !deckId) {
      showErrorToast("Invalid slide or deck ID");
      setDeletePrompt(false);
      return;
    }

    setDeletePrompt(false);
    setSaveLoading(true);

    deleteSlidesRequest(deckId, slideId, (response, err, errorCode) => {
      setSaveLoading(false);
      if (err) {
        showErrorToast("Deletion failed. Please try again");
      } else {
        showSuccessToast("Deletion successful");

        // Update slides list
        let tempSlides = [...slides];
        tempSlides.splice(deleteSlideIndex, 1);

        // Update indices
        tempSlides.forEach((slide, idx) => {
          if (slide.index !== undefined) {
            slide.index = idx + 1;
          }
        });

        setSlides(tempSlides);

        // If deleted slide was selected, select another one
        if (deleteSlideIndex === selectedSlideIndex) {
          if (deleteSlideIndex > 0) {
            // Select previous slide
            const newIndex = deleteSlideIndex - 1;
            handleSlideClick(tempSlides[newIndex], newIndex);
          } else if (tempSlides.length > 0) {
            // Select first slide
            handleSlideClick(tempSlides[0], 0);
          }
        } else if (deleteSlideIndex < selectedSlideIndex) {
          // Adjust selected index if needed
          setSelectedSlideIndex(selectedSlideIndex - 1);
        }

        // Update deck data
        if (deckData) {
          setDeckData((prev) => ({
            ...prev,
            slides: tempSlides,
          }));
        }

        // Clear delete state
        setSlideToDelete(null);
        setDeleteSlideIndex(null);
      }
    });
  };

  // Get coordinates relative to HTML content
  const getHtmlCoordinates = (e) => {
    // Fallback to wrapper if iframe not available
    if (!htmlWrapperRef.current) return { x: 0, y: 0 };
    const wrapper = htmlWrapperRef.current;
    const rect = wrapper.getBoundingClientRect();
    // Use contentScale for accurate coordinate calculation
    const scale = contentScale || 1;
    const clientX = e.clientX - rect.left;
    const clientY = e.clientY - rect.top;
    // Account for wrapper's transform scale
    const x = clientX / scale + wrapper.scrollLeft / scale;
    const y = clientY / scale + wrapper.scrollTop / scale;
    return { x: Math.max(0, Math.min(x, 1024)), y: Math.max(0, y) };
  };

  const handleHtmlMouseDown = (e) => {
    if (!htmlWrapperRef.current || toolMode !== "select") return;

    // Don't start selection if clicking on an embedded link area
    if (
      e.target.classList?.contains("pdf-selected-area-link") ||
      e.target.closest?.(".pdf-selected-area-link")
    ) {
      return;
    }

    // Get exact click coordinates first - this is critical for accurate selection
    const { x, y } = getHtmlCoordinates(e);

    // Don't start selection if clicking on an existing overlay area
    const clickedArea = selectedAreas.find((area) => {
      // More accurate boundary checking
      const left = Math.min(area.x, area.x + area.width);
      const right = Math.max(area.x, area.x + area.width);
      const top = Math.min(area.y, area.y + area.height);
      const bottom = Math.max(area.y, area.y + area.height);
      // Use strict boundaries (exclude edges to avoid accidental clicks)
      return x > left && x < right && y > top && y < bottom;
    });

    if (clickedArea) {
      // Clicked on existing area - select it for linking
      e.preventDefault();
      e.stopPropagation();
      setSelectedAreaForLink(clickedArea.id);
      if (clickedArea.linkType === "url") {
        setLinkType("url");
        setLinkUrl(clickedArea.linkUrl || "");
        setLinkSlideId("");
      } else if (clickedArea.linkSlideId) {
        setLinkType("slide");
        setLinkSlideId(clickedArea.linkSlideId || "");
        setLinkUrl("");
      } else {
        setLinkType("slide");
        setLinkUrl("");
        setLinkSlideId("");
      }
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    isDrawingRef.current = true;
    setIsDrawing(true); // Update state to trigger re-render
    // Start selection exactly at click position
    startPosRef.current = { x, y };
    selectionRef.current = { x, y, width: 0, height: 0 };
  };

  const handleHtmlMouseMove = (e) => {
    if (
      !isDrawingRef.current ||
      !htmlWrapperRef.current ||
      toolMode !== "select"
    )
      return;

    e.preventDefault();
    e.stopPropagation();

    // Get current mouse position accurately
    const { x, y } = getHtmlCoordinates(e);

    if (selectionRef.current && startPosRef.current) {
      // Calculate width and height from exact start position to current position
      // This ensures selection follows mouse drag exactly
      selectionRef.current.x = Math.min(startPosRef.current.x, x);
      selectionRef.current.y = Math.min(startPosRef.current.y, y);
      selectionRef.current.width = Math.abs(x - startPosRef.current.x);
      selectionRef.current.height = Math.abs(y - startPosRef.current.y);
      // Force re-render to show selection rectangle
      setSelectedAreas((prev) => [...prev]);
    }
  };

  const handleHtmlMouseUp = (e) => {
    if (!isDrawingRef.current || toolMode !== "select") {
      isDrawingRef.current = false;
      setIsDrawing(false); // Update state to trigger re-render
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (selectionRef.current && startPosRef.current) {
      const { x, y } = getHtmlCoordinates(e);

      // Calculate final selection from start position to current position
      const startX = startPosRef.current.x;
      const startY = startPosRef.current.y;
      const finalX = x;
      const finalY = y;

      const width = Math.abs(finalX - startX);
      const height = Math.abs(finalY - startY);

      // Only create selection if it has meaningful size
      if (width > 5 && height > 5) {
        const selection = {
          x: Math.min(startX, finalX),
          y: Math.min(startY, finalY),
          width: width,
          height: height,
          id: `area-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        };
        // Use functional update to avoid duplicate selections
        setSelectedAreas((prev) => {
          // Check if this selection already exists (prevent duplicates by checking ID and position)
          const exists = prev.some(
            (area) =>
              area.id === selection.id ||
              (Math.abs(area.x - selection.x) < 3 &&
                Math.abs(area.y - selection.y) < 3 &&
                Math.abs(area.width - selection.width) < 3 &&
                Math.abs(area.height - selection.height) < 3)
          );
          if (exists) {
            console.log("Duplicate selection prevented");
            return prev;
          }
          return [...prev, selection];
        });
        // Auto-select the newly created area for linking
        setSelectedAreaForLink(selection.id);
        setLinkType("slide");
        setLinkUrl("");
        setLinkSlideId("");
      }
      selectionRef.current = null;
    }

    isDrawingRef.current = false;
    setIsDrawing(false); // Update state to trigger re-render
  };

  const drawSelection = () => {
    if (!canvasRef.current || !selectionRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = imageRef.current;

    if (!img || !img.complete) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Draw existing selections
    selectedAreas.forEach((area) => {
      ctx.strokeStyle = "#007bff";
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(area.x, area.y, area.width, area.height);
    });

    // Draw current selection
    if (selectionRef.current) {
      ctx.strokeStyle = "#007bff";
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(
        selectionRef.current.x,
        selectionRef.current.y,
        selectionRef.current.width,
        selectionRef.current.height
      );
    }
  };

  const eraseSelectedArea = (x, y) => {
    // Find the selected area that contains this point and remove it
    const areaToRemove = selectedAreas.find((area) => {
      // More accurate boundary checking
      const left = Math.min(area.x, area.x + area.width);
      const right = Math.max(area.x, area.x + area.width);
      const top = Math.min(area.y, area.y + area.height);
      const bottom = Math.max(area.y, area.y + area.height);
      // Use strict boundaries
      return x > left && x < right && y > top && y < bottom;
    });

    if (areaToRemove) {
      setLinkSlideId("");
      setLinkUrl("");
      // Remove the area from selectedAreas
      const updatedAreas = selectedAreas.filter(
        (area) => area.id !== areaToRemove.id
      );
      setSelectedAreas(updatedAreas);
      if (baseHtmlRef.current) {
        const updatedHtml = modifyHtmlWithSelectedAreas(
          baseHtmlRef.current,
          updatedAreas
        );
        const container = document.getElementById("wrapper");
        container.innerHTML = updatedHtml;
        setSlideHtml(updatedHtml);
        attachSlide();
      }
      // HTML will be updated automatically by useEffect

      // Clear selected area for link if it was the erased one
      if (selectedAreaForLink === areaToRemove.id) {
        setSelectedAreaForLink(null);
        setLinkSlideId("");
        setLinkUrl("");
      }

      // Redraw canvas without the deleted selection
      if (canvasRef.current && imageRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const img = imageRef.current;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Redraw remaining selections
        updatedAreas.forEach((area) => {
          ctx.strokeStyle = "#007bff";
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(area.x, area.y, area.width, area.height);
        });
      }

      // Remove modals
      const htmlString = baseHtmlRef.current;
      if (!htmlString) return;
      const container = document.createElement("div");
      container.innerHTML = htmlString;

      const validModalIds = new Set(
        updatedAreas.map((area) => `pdf-link-${area.id}`)
      );
      const modals = container.querySelectorAll(".modal.pdf-select-area-modal");
      modals.forEach((modal) => {
        const modalId = modal.getAttribute("data-id");
        if (!validModalIds.has(modalId)) {
          modal.remove();
        }
      });
      baseHtmlRef.current = container.innerHTML;
    }
  };

  const handleImageLoad = () => {
    if (!canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const img = imageRef.current;

    // Set canvas internal size to match image
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;

    const ctx = canvas.getContext("2d");
    // Clear and draw image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Redraw selections
    selectedAreas.forEach((area) => {
      ctx.strokeStyle = "#007bff";
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(area.x, area.y, area.width, area.height);
    });
  };

  const handleDeleteSelection = (areaId) => {
    const updatedAreas = selectedAreas.filter((area) => area.id !== areaId);
    setSelectedAreas(updatedAreas);
    // HTML will be updated automatically by useEffect

    // Clear selected area for link if it was the deleted one
    if (selectedAreaForLink === areaId) {
      setSelectedAreaForLink(null);
      setLinkSlideId("");
      setLinkUrl("");
    }

    // Redraw canvas without deleted selection
    if (canvasRef.current && imageRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const img = imageRef.current;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      updatedAreas.forEach((area) => {
        ctx.strokeStyle = "#007bff";
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(area.x, area.y, area.width, area.height);
      });
    }
  };

  // Function to modify HTML to embed selected areas as clickable elements with dotted borders
  // This embeds areas immediately so they persist after refresh
  const modifyHtmlWithSelectedAreas = (html, areas, isEditor = true) => {
    if (!html) return html;

    // Create a temporary container to parse and modify HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // First, remove any previously embedded areas to avoid duplicates
    const embeddedAreas = tempDiv.querySelectorAll(".pdf-selected-area-link");
    embeddedAreas.forEach((area) => area.remove());

    const pdfLinks = tempDiv.querySelectorAll('[ccid^="pdf-link-"]');
    pdfLinks.forEach((link) => link.remove());

    // If no areas to embed, return cleaned HTML
    if (!areas || areas.length === 0) {
      return tempDiv.innerHTML;
    }

    // Sort areas by z-index (later areas on top)
    const sortedAreas = [...areas].sort((a, b) => {
      // Areas with links should be on top
      if (a.linkSlideId || a.linkUrl) return 1;
      if (b.linkSlideId || b.linkUrl) return -1;
      return 0;
    });

    // Add clickable <a> tags for each selected area with dotted borders
    sortedAreas.forEach((area, index) => {
      // Create <a> tag for proper link behavior
      const clickableLink = document.createElement("a");
      clickableLink.style.position = "absolute";
      clickableLink.style.left = `${area.x}px`;
      clickableLink.style.top = `${area.y}px`;
      clickableLink.style.width = `${Math.abs(area.width)}px`;
      clickableLink.style.height = `${Math.abs(area.height)}px`;
      clickableLink.style.cursor = isEditor ? "default":  "pointer";
      clickableLink.style.zIndex = "1000";
      clickableLink.style.pointerEvents = "auto";
      clickableLink.style.display = "block";
      clickableLink.style.textDecoration = "none";
      // Add dotted border styling for editor view (will be removed in preview via CSS)
      clickableLink.style.border = "1px dashed #54565B";
      clickableLink.style.borderStyle = "dashed";
      clickableLink.style.borderWidth = "1px";
      clickableLink.style.borderColor = "#54565B";
      clickableLink.style.backgroundColor = "rgba(84, 86, 91, 0.05)";
      clickableLink.style.boxSizing = "border-box";

      // Add link attributes - use <a> tag properly for preview navigation
      if (area.linkSlideId) {
        clickableLink.id = `${area.linkSlideId}-${index}`;
        clickableLink.setAttribute("data-slide-id", area.linkSlideId);
        clickableLink.setAttribute("masterid", `pdf-link-${area.id}`);
        clickableLink.setAttribute("data-area-id", area.id);
        // For preview, use href that can be handled by preview navigation
        // The preview will use data-slide-id to navigate
        // clickableLink.setAttribute("href", `#slide-${area.linkSlideId}`);
        /*  commented to remove the popup concept on click of selected area
        // Append / Update Modal
        const body = tempDiv.querySelector("body") || tempDiv;
        const modalId = `pdf-link-${area.id}`;
        let modal = body.querySelector(`[data-id='${modalId}']`);
        if (!modal) {
          // CREATE modal
          modal = document.createElement("div");
          body.appendChild(modal);
        }
        // UPDATE modal
        modal.className = "modal pdf-select-area-modal";
        modal.setAttribute("data-modal-type", "pdf-select-area-modal");
        modal.setAttribute("data-id", modalId);
        modal.setAttribute("data-slide-id", area.linkSlideId);
        modal.setAttribute("data-deck-id", deckId);
        modal.style.display = "none";
        // Ensure content
        modal.innerHTML = `
          <div class="modal-content">
            <span class="close">
              <button style="cursor:pointer">&times;</button>
            </span>
          </div>
        `; */
      } else if (area.linkUrl) {
        if(!isEditor){
          clickableLink.setAttribute("href", area.linkUrl);
        }
        clickableLink.setAttribute("target", "_blank");
        clickableLink.setAttribute("rel", "noopener noreferrer");
        clickableLink.setAttribute("data-area-id", area.id);
      } else {
        // No link yet - still show as <a> tag but make it non-clickable in preview
        clickableLink.setAttribute("href", "#");
        clickableLink.setAttribute("data-area-id", area.id);
        clickableLink.style.pointerEvents = "auto"; // Keep clickable for editing
        clickableLink.style.cursor = "default";
      }

      // Add class for styling
      clickableLink.className = "pdf-selected-area-link";

      // Append to body or wrapper
      const wrapper = tempDiv.querySelector("#wrapper");
      if (wrapper) {
        // Append inside wrapper element
        wrapper.appendChild(clickableLink);
      } else {
        // Fallback to body or tempDiv if wrapper doesn't exist
        const body = tempDiv.querySelector("body") || tempDiv;
        body.appendChild(clickableLink);
      }
    });

    return tempDiv.innerHTML;
  };

  // Keep ref in sync with selectedAreas to avoid stale closures
  useEffect(() => {
    selectedAreasRef.current = selectedAreas;
  }, [selectedAreas]);

  // Effect to update HTML when selectedAreas change
  useEffect(() => {
    if (baseHtmlRef.current) {
      // Always use the latest selectedAreas from ref to avoid stale closures
      const areasToUse = selectedAreasRef.current || selectedAreas || [];
      const updatedHtml = modifyHtmlWithSelectedAreas(
        baseHtmlRef.current,
        areasToUse
      );
      setSlideHtml(updatedHtml);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAreas, slideCss]);

  // Function to create simplified editorData structure for PDF editor
  const createEditorData = () => {
    // Create a minimal editorData structure matching the normal editor format
    const editorData = {
      assets: [],
      styles: [],
      pages: [
        {
          frames: [
            {
              component: {
                type: "wrapper",
                attributes: { id: "pdf-wrapper" },
                components: [],
              },
            },
          ],
          type: "main",
          id: `pdf-${selectedSlide?.slideid?._id || Date.now()}`,
        },
      ],
    };

    return JSON.stringify(editorData);
  };

  const handleSave = () => {
    if (!selectedSlide?.slideid?._id) {
      showErrorToast("No slide selected to save");
      return;
    }

    setSaveLoading(true);

    // Use base HTML and latest selectedAreas from ref to ensure all areas are included
    const baseHtml = baseHtmlRef.current || slideHtml;
    // Always use the ref which is kept in sync with state, fallback to state if ref is empty
    const areasToSave =
      selectedAreasRef.current && selectedAreasRef.current.length > 0
        ? selectedAreasRef.current
        : selectedAreas && selectedAreas.length > 0
        ? selectedAreas
        : [];

    console.log("Saving areas:", areasToSave);
    console.log("SelectedAreas state:", selectedAreas);
    console.log("SelectedAreasRef current:", selectedAreasRef.current);

    // Modify HTML to embed selected areas as clickable elements - use latest selectedAreas
    const modifiedHtml = modifyHtmlWithSelectedAreas(baseHtml, areasToSave, false);

    // Build CSS with link styles - remove borders for preview
    const linkCss = `
      .pdf-selected-area-link {
        cursor: pointer;
        border: none !important;
        background-color: transparent !important;
      }
      .pdf-selected-area-link:hover {
        background-color: rgba(84, 86, 91, 0.05) !important;
      }
      [data-slide-id] {
        cursor: pointer;
      }
    `;
    const modifiedCss = slideCss + linkCss;

    // Prepare payload matching normal editor format - selectedAreas inside content
    const payload = {
      // Include full content matching normal editor structure
      content: {
        editorData: createEditorData(),
        html: modifiedHtml || "",
        css: modifiedCss || "",
        scripts: script || "",
        thirdPartyPlugins: thirdPartyPlugins || [],
        extraContent: [],
        // Include selected areas inside content - use areasToSave to ensure all areas are included
        selectedAreas: (areasToSave || []).map((area) => ({
          x: area.x,
          y: area.y,
          width: area.width,
          height: area.height,
          linkType: area.linkType || null,
          linkUrl: area.linkUrl || null,
          linkSlideId: area.linkSlideId || null,
        })),
      },
      // Include thumbnail
      thumbnail:
        selectedSlide?.slideid?.thumbnailNew ||
        selectedSlide?.slideid?.thumbnail ||
        "",
    };

    console.log("PDF Editor Save Payload (matching normal editor):", payload);

    // Call API to save selected areas and content
    updatSlideData(
      selectedSlide.slideid._id,
      payload,
      (response, err, errorCode) => {
        setSaveLoading(false);
        if (err) {
          showErrorToast("Failed to save changes. Please try again.");
          console.error("Error saving slide:", err);
        } else {
          showSuccessToast("Changes saved successfully");

          // ✅ IMPORTANT FIX
          // Update base HTML so next save keeps ALL existing links
          baseHtmlRef.current = modifiedHtml;

          // Keep slideHtml in sync as well
          setSlideHtml(modifiedHtml);
        }
      }
    );
  };

  const handleDownloadHTML = () => {
    if (!deckId && !deckData?._id) {
      showErrorToast("Deck ID not found");
      return;
    }

    const deckIdToUse = deckId || deckData?._id;
    setDownloadLoading(true);

    ExportDeckToMail(deckIdToUse, (response, err, errorCode) => {
      setDownloadLoading(false);
      if (err) {
        showErrorToast("Download failed. Please try again.");
      } else {
        showSuccessToast("A link to download the HTML will be emailed.", undefined, undefined, 10000);
      }
    });
  };

  const handleZoomIn = () => {
    if (scalePercent > 50) {
      setScalePercent(scalePercent - 10);
    }
  };

  const handleZoomOut = () => {
    if (scalePercent < 200) {
      setScalePercent(scalePercent + 10);
    }
  };

  const handleResetZoom = () => {
    setScalePercent(100);
  };

  const handlePrevSlide = () => {
    if (selectedSlideIndex > 0) {
      const newIndex = selectedSlideIndex - 1;
      handleSlideClick(slides[newIndex], newIndex);
    }
  };

  const handleNextSlide = () => {
    if (selectedSlideIndex < slides.length - 1) {
      const newIndex = selectedSlideIndex + 1;
      handleSlideClick(slides[newIndex], newIndex);
    }
  };

  const handleFlowCreator = async () => {
    // Just open the flow creator UI with existing slides
    // Same UI as existing screen, just pass slide IDs
    setFlowCreatorLoading(true);

    // If we have slides, just open the panel
    if (slides && slides.length > 0) {
      setTimeout(() => {
        setShowAllSlides(!showAllSlides);
        setFlowCreatorLoading(false);
      }, 100);
    } else {
      // If no slides, load deck data first
      const deckIdToUse = deckId || deckData?._id;

      if (!deckIdToUse) {
        setFlowCreatorLoading(false);
        showErrorToast("Deck ID not found");
        return;
      }

      await geteDetailByID(
        deckIdToUse,
        async (response, err, errorCode) => {
          if (err) {
            setFlowCreatorLoading(false);
            showErrorToast("Failed to load slides");
          } else {
            let data = await response.data;
            setDeckData(data);
            setSlides(data.slides || []);
            setTimeout(() => {
              setShowAllSlides(!showAllSlides);
              setFlowCreatorLoading(false);
            }, 100);
          }
        },
        true // skipStatus
      );
    }
  };

  const handlePreview = () => {
    if (!deckId && !deckData?._id) {
      showErrorToast("Deck ID not found");
      return;
    }

    setPreviewLoading(true);
    setPreviewUrlReady(false); // Reset URL ready state
    // Don't open modal immediately - wait for API success
    const deckIdToUse = deckId || deckData?._id;

    // First save the current changes before previewing
    const areasToPreview = selectedAreasRef.current || selectedAreas || [];
    if (selectedSlide?.slideid?._id && areasToPreview.length > 0) {
      // Use base HTML and latest selectedAreas from ref to ensure all areas are included
      const baseHtml = baseHtmlRef.current || slideHtml;
      // Modify HTML to embed selected areas as clickable elements - use latest selectedAreas
      const modifiedHtml = modifyHtmlWithSelectedAreas(
        baseHtml,
        areasToPreview,
        false
      );

      // Build CSS with link styles - hide borders in preview
      const linkCss = `
        .pdf-selected-area-link {
          cursor: pointer;
          border: none !important;
          background-color: transparent !important;
        }
        .pdf-selected-area-link:hover {
          background-color: rgba(84, 86, 91, 0.05) !important;
        }
        [data-slide-id] {
          cursor: pointer;
        }
      `;
      const modifiedCss = slideCss + linkCss;

      // Prepare payload matching normal editor format - selectedAreas inside content
      const payload = {
        // Include full content matching normal editor structure
        content: {
          editorData: createEditorData(),
          html: modifiedHtml || "",
          css: modifiedCss || "",
          scripts: script || "",
          thirdPartyPlugins: thirdPartyPlugins || [],
          extraContent: [],
          // Include selected areas inside content - ensure ALL areas are saved
          selectedAreas: (areasToPreview || []).map((area) => ({
            x: area.x,
            y: area.y,
            width: area.width,
            height: area.height,
            linkType: area.linkType || null,
            linkUrl: area.linkUrl || null,
            linkSlideId: area.linkSlideId || null,
          })),
        },
        // Include thumbnail
        thumbnail:
          selectedSlide?.slideid?.thumbnailNew ||
          selectedSlide?.slideid?.thumbnail ||
          "",
      };

      console.log(
        "PDF Editor Preview Save Payload (matching normal editor):",
        payload
      );

      updatSlideData(
        selectedSlide.slideid._id,
        payload,
        (saveResponse, saveErr) => {
          // Continue with preview even if save fails (user can retry)
          if (saveErr) {
            console.warn("Failed to save before preview:", saveErr);
          }

          // Call the same preview API as used in the editor
          ExportDeckToMail(
            deckIdToUse,
            (response, err, errorCode) => {
              if (err) {
                setPreviewLoading(false);
                setPreviewUrlReady(false);
                showErrorToast(`Preview Failed. ${err}`);
                // Don't open modal on error
              } else {
                setPreviewLoading(false);
                setPreviewUrlReady(true);
                // Only open modal after successful API call
                setShowPreView(true);
              }
            },
            "preview=true"
          );
        }
      );
    } else {
      // No changes to save, directly call preview
      ExportDeckToMail(
        deckIdToUse,
        (response, err, errorCode) => {
          if (err) {
            setPreviewLoading(false);
            setPreviewUrlReady(false);
            showErrorToast(`Preview Failed. ${err}`);
            // Don't open modal on error
          } else {
            setPreviewLoading(false);
            setPreviewUrlReady(true);
            // Only open modal after successful API call
            setShowPreView(true);
          }
        },
        "preview=true"
      );
    }
  };

  // Handle metadata name change
  const handleMetadataNameChange = (e) => {
    const value = e.target.value;
    if (value.length > 30) {
      setMetadataHelperText("e-Detail name exceeded the maximum length");
      setMetadataErrorMsg(true);
      return;
    }
    const specialChars = /[`!@#$%^&*+=\[\]{};':"\\|,.<>/?~]/;
    if (specialChars.test(value) || value.includes(" ")) {
      setMetadataHelperText(
        "Name can only contain alphanumeric characters, hyphens (-), and underscores (_)."
      );
      setMetadataErrorMsg(true);
      return;
    }
    if (value.trim().length < 3 && value.length > 0) {
      setMetadataHelperText("Please use minimum 3 characters.");
      setMetadataErrorMsg(true);
    } else {
      setMetadataHelperText("");
      setMetadataErrorMsg(false);
    }
    setMetadataName(value);
  };

  // Validate metadata name
  const validateMetadataName = async () => {
    if (metadataName.trim().length < 3 || metadataName.trim().length > 30) {
      return false;
    }
    if (metadataName === existingMetadataName) {
      return true;
    }
    try {
      const valid = await validateDeckName({ name: metadataName }, 2);
      if (valid?.success) {
        setMetadataErrorMsg(false);
        setMetadataHelperText("");
        return true;
      } else {
        if (valid?.err) {
          const errMsg = valid.err;
          setMetadataHelperText(
            errMsg.hasOwnProperty("message") ? errMsg.message : errMsg
          );
          setMetadataErrorMsg(true);
        }
        return false;
      }
    } catch (error) {
      console.error("Error validating name:", error);
      return false;
    }
  };

  const publishHTML = () => {
    const veevaMetadata = deckData.veevaMetadata;
    let alreadyPublished = false;
    if (
      veevaMetadata &&
      Array.isArray(veevaMetadata) &&
      veevaMetadata.length > 1
    ) {
      for (let i = 0; i < veevaMetadata.length; i++) {
        const metaData = veevaMetadata[i];
        const assetType = metaData.assetType || null;
        const deckType = metaData.deckType || null;
        if (assetType && assetType === 2 && deckType && deckType === 2) {
          alreadyPublished = true;
          break;
        }
      }
    }

    const deckIdToUse = deckId || deckData?._id;
    if (!alreadyPublished) {
      setShowMetadataModal(true);
    } else {
      publishToVeeva(deckIdToUse);
    }

    // Fetch traceability matrix data when opening modal
    if (deckIdToUse) {
      getDeckTraceabilityMatrix(deckIdToUse, (response, err, errorCode) => {
        if (err) {
          console.error("Error fetching traceability matrix:", err);
          setTraceabilityData(null);
        } else {
          setTraceabilityData(response?.data);
        }
      });
    }
  };

  // Handle metadata publish
  const handleMetadataPublish = async () => {
    if (metadataName.trim().length < 3) {
      setMetadataHelperText("Please use minimum 3 characters");
      setMetadataErrorMsg(true);
      return;
    }
    if (metadataName.trim().length > 30) {
      setMetadataHelperText("e-Detail name exceeded the maximum length");
      setMetadataErrorMsg(true);
      return;
    }

    const valid = await validateMetadataName();
    if (!valid) {
      return;
    }

    if (!deckId && !deckData?._id) {
      showErrorToast("Deck ID not found");
      return;
    }

    setMetadataLoading(true);
    const deckIdToUse = deckId || deckData?._id;

    // First update deck name if changed
    const nameChanged = metadataName !== deckData?.name;
    if (nameChanged) {
      updateDeckInfo(
        deckIdToUse,
        { name: metadataName },
        (response, err, errorCode) => {
          if (err) {
            setMetadataLoading(false);
            if (errorCode === 400) {
              showErrorToast(
                err.response?.data?.message || "Failed to update metadata"
              );
            } else {
              showErrorToast("Failed to update metadata");
            }
            return;
          } else {
            // Update local deckData
            if (deckData) {
              setDeckData({ ...deckData, name: metadataName });
            }
            if (appData?.setDeckId) {
              appData.setDeckId({ ...appData.deckId, name: metadataName });
            }
            // Continue to publish
            publishToVeeva(deckIdToUse);
          }
        }
      );
    } else {
      // Name not changed, directly publish
      publishToVeeva(deckIdToUse);
    }
  };

  // Publish to Veeva
  const publishToVeeva = (deckIdToUse) => {
    const stopLoading = () => {
      if (showMetadataModal === false) {
        setPublishPending(false);
      } else {
        setMetadataLoading(false);
      }
    };

    if (showMetadataModal === false) {
      setPublishPending(true);
    } else {
      setMetadataLoading(true);
    }

    PublishHTMLAndPDFBinder(deckIdToUse, (binderResponse, err, errCode) => {
      if (err) {
        showErrorToast(
          err?.response?.data?.err ||
            "No deck was found with the requested deck id"
        );
        stopLoading();
        return;
      }

      if (
        binderResponse?.status === 200 &&
        binderResponse?.data?.success === true
      ) {
        PublishHTMLAndPDF(
          deckIdToUse,
          (publishResponse, publishErr, publishErrorCode) => {
            stopLoading();

            if (publishErr) {
              showErrorToast("Failed to generate zip file. Please try again.");
              return;
            }

            showSuccessToast(
              "Publish is in progress. An email notification will be sent to you once the HTML has been published.", undefined, undefined, 10000
            );

            setShowMetadataModal(false);
            navigate("/");
          }
        );
      } else {
        stopLoading();
        showErrorToast("Failed to publish deck. Please try again.");
      }
    });
  };

  // Close metadata modal
  const closeMetadataModal = () => {
    // Reset to current deck name when closing
    if (deckData?.name) {
      setMetadataName(deckData.name);
    } else {
      setMetadataName(generateDeckName("pdf_eda"));
    }
    setMetadataHelperText("");
    setMetadataErrorMsg(false);
    setTraceabilityData(null);
    setShowMetadataModal(false);
  };

  if (loading) {
    return (
      <div className="pdf-editor-loading">
        <div>Loading PDF Editor...</div>
      </div>
    );
  }

  const attachSlide = () => {
    const deckIdToUse = deckId || deckData?._id;
    const slideId = selectedSlide?.slideid?._id;
    let string = "";
    const selectedIDlist = document.querySelectorAll(".pdf-selected-area-link");
    selectedIDlist.forEach((ele) => {
      if (ele.getAttribute("data-slide-id")) {
        string += `
      $("#${ele.id}").on('click', function(){
        let btnId = $(this).attr('data-slide-id')
        btnArray.map((btnObj)=>{
            if(btnObj._id===btnId){
                console.log(btnObj.name)
                /* added for master to master linking in desktop and ipad both */
                if(window.navigator.maxTouchPoints > 1 && /MacIntel|Mac/.test(navigator.userAgent)){
                  com.veeva.clm.runAPIRequest("veeva:gotoSlide("+btnObj.name+".zip)"); 
                }else{
                  window.parent.postMessage(
                    {
                      action: "changeIframeSrc",
                      src: btnObj.name
                    },
                    "*" // parent domain
                  );
                }
                /*  */
            }
          })
        })`;
      }
    });

    let finalString = `$(document).ready(function () { ${string} });`;
    postAddCutomScripts(
      { navigationScript: finalString },
      deckIdToUse,
      slideId,
      (res, err) => {
        if (err) {
        } else {
        }
      }
    );
  };

  return (
    <div className="editor-wrapper pdf-editor-wrapper">
      {/* Loader for save and preview operations */}
      <CircularProgressBar
        isloading={
          saveLoading ||
          previewLoading ||
          flowCreatorLoading ||
          reorderLoading ||
          deckLoading ||
          downloadLoading ||
          publishPending
        }
      />
      {/* Header */}
      <div
        className="d-flex main-header import-pdf-header"
        style={{
          height: "64px",
          borderBottom: "1px solid #e3e3e3",
          backgroundColor: "#fff",
          alignItems: "center",
        }}
      >
        <div
          className="editor-wrapper-header1 header-left"
          style={{ display: "flex", alignItems: "center", margin: 0 }}
        >
          <div
            className="home-icon"
            onClick={() => navigate("/")}
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          >
            <div className="home-icon-wrapper" style={{ margin: "12px" }}>
              <HomeIcon />
            </div>
            <div className="home-icon-separator"></div>
            {/* Removed separator and adjusted text layout */}
            <div className="home-icon-text">
              <div
                className="header-title"
                style={{ fontSize: "12px", color: "#666" }}
              >
                Create e-Detail
              </div>
              <div className="header-subtitle">{"eDetail"}</div>
            </div>
          </div>
          {/* Save Button relocated to Left */}
          <div
            className="editor-wrapper-header-content1 d-flex"
            onClick={handleSave}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "#0050b5",
              fontWeight: "500",
              // marginLeft: "16px",
              // borderLeft: "1px solid #e3e3e3",
              // paddingLeft: "20px",
              height: "40px",
              opacity: saveLoading ? 0.6 : 1,
            }}
          >
            <CustomTooltip title="Save" classes="editor-wrapper-save-popover">
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <img
                  src={SaveChangesNewIcon}
                  width={99}
                  height={20}
                  alt="Save"
                />
              </div>
            </CustomTooltip>
          </div>
        </div>
        <div style={{ flex: "1" }} className="editor-wrapper-header2">
          <div className="d-flex align-items-center" style={{ gap: "12px" }}>
            {/* Spacing spacer */}
            <div style={{ flex: 1 }}></div>

            <ul
              className="d-flex"
              style={{
                margin: 0,
                padding: 0,
                listStyle: "none",
                alignItems: "center",
                gap: "2px",
              }}
            >
              <li className="zoom-in-out">
                <div
                  className="editor-wrapper-zoominout"
                  style={{
                    // border: "1px solid #e3e3e3",
                    padding: "4px 10px",
                    // borderRadius: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <img
                    src={ZoomInNewIcon}
                    alt="zoom In"
                    className="zoom-in"
                    onClick={handleZoomIn}
                    width={16}
                    height={16}
                    style={{ cursor: "pointer" }}
                  />
                  <span
                    className="persent-display"
                    onDoubleClick={handleResetZoom}
                    style={{
                      cursor: "pointer",
                      minWidth: "40px",
                      textAlign: "center",
                      fontSize: "12px",
                      color: "#666",
                      borderBottom: "1px solid #DDDFDD",
                    }}
                    title="Double-click to reset to 100%"
                  >
                    {`${scalePercent}%`}
                  </span>
                  <img
                    src={ZoomOutNewIcon}
                    alt="zoom Out"
                    className="zoom-out"
                    onClick={handleZoomOut}
                    width={16}
                    height={16}
                    style={{ cursor: "pointer" }}
                  />
                </div>
              </li>
              <li>
                <div
                  style={{
                    backgroundColor: "#DDDFDD",
                    width: "1px",
                    height: "22px",
                  }}
                ></div>
              </li>
              <li className="listItems" style={{ paddingRight: 0 }}>
                <CustomTooltip
                  title="Preview"
                  classes="editor-wrapper-preview-popover"
                >
                  <img
                    alt="Preview"
                    src={PreviewNewIcon}
                    style={{
                      width: "18px",
                      height: "18px",
                      cursor: "pointer",
                      paddingRight: "0[x",
                      opacity: previewLoading ? 0.6 : 1,
                    }}
                    className="infoIconL"
                    onClick={handlePreview}
                  />
                </CustomTooltip>
              </li>
            </ul>
            <button
              onClick={handleDownloadHTML}
              style={{
                padding: "6px 14px",
                backgroundColor: "#fff",
                border: "1px solid #203662",
                color: "#203662",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "700",
                cursor: "pointer",
                opacity: downloadLoading ? 0.6 : 1,
                // marginRight: "12px",
                transition: "all 0.2s",
              }}
            >
              DOWNLOAD HTML
            </button>
            <button
              disabled={publishPending}
              onClick={publishHTML}
              style={{
                padding: "8px 16px",
                backgroundColor: "#00306b",
                border: "none",
                color: "#fff",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              PUBLISH HTML
            </button>
          </div>
        </div>
      </div>

      <div className="editor-content-wrapper">
        {/* Sidebar Toolbar (Moved to far left) */}
        <div className="pdf-editor-toolbar">
          {/* New Toggle Button - Controls Left Panel */}
          <CustomTooltip title="Toggle Slides Panel" placement="right">
            <button
              className={`tool-btn ${isLeftPanelOpen ? "active" : ""}`}
              onClick={() => setIsLeftPanelOpen(!isLeftPanelOpen)}
              style={{ marginBottom: "12px" }}
            >
              <img
                src={isLeftPanelOpen ? SlidsIconActive : SlidsIcon}
                alt="Toggle Panel"
              />
            </button>
          </CustomTooltip>
        </div>

        {/* Left Sidebar - Draggable Slides */}
        <div
          className={`left-section-partition pdf-editor-left-panel ${
            !isLeftPanelOpen ? "pdf-editor-left-panel-hidden" : ""
          }`}
        >
          <div className="slides-panel-header">
            <div className="slides-panel-header-top">
              <h3>All Slides ({slides.length})</h3>
            </div>
            {isLeftPanelOpen && (
              <p className="slides-note">
                Note: Drag & drop to rearrange the Slide
              </p>
            )}
          </div>
          {isLeftPanelOpen && (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="slides">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="slides-list"
                  >
                    {slides.map((slide, index) => (
                      <Draggable
                        key={slide.slideid?._id || index}
                        draggableId={slide.slideid?._id || `slide-${index}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`slide-item ${
                              selectedSlideIndex === index ? "selected" : ""
                            } ${snapshot.isDragging ? "dragging" : ""}`}
                            onClick={() => handleSlideClick(slide, index)}
                          >
                            <div className="slide-item-content">
                              <div className="slide-top-row">
                                <span className="slide-number">
                                  {slide.index}.
                                </span>
                              </div>
                              <div className="slide-middle-row">
                                <div
                                  className="drag-handle"
                                  {...provided.dragHandleProps}
                                >
                                  <img
                                    src={DragIndicatorIcon}
                                    alt="Drag"
                                    style={{
                                      width: "17.14px",
                                      height: "16px",
                                      flexShrink: 0,
                                      objectFit: "contain",
                                    }}
                                  />
                                </div>
                                <div className="slide-thumbnail">
                                  <img
                                    src={
                                      slide.slideid?.thumbnailNew ||
                                      slide.slideid?.thumbnail ||
                                      ""
                                    }
                                    alt={`Slide ${index + 1}`}
                                  />
                                </div>
                              </div>
                              <div className="slide-bottom-row">
                                <div className="slide-name">
                                  <span classes="slide-text-name">Name:</span>
                                  <img
                                    src={EditIconSimple}
                                    alt="Rename"
                                    className="edit-name-icon"
                                    width="12"
                                    onClick={(e) =>
                                      handleInlineEdit(slide, index, "name", e)
                                    }
                                    style={{ cursor: "pointer" }}
                                  />
                                  {editingSlideIndex === index &&
                                  editingField === "name" ? (
                                    <input
                                      name="name"
                                      className="message-input"
                                      style={{
                                        fontSize: "13px",
                                        padding: "2px 4px",
                                        // border: "1px solid #0050b5",
                                        borderRadius: "4px",
                                        width: "100%",
                                        maxWidth: "140px",
                                      }}
                                      placeholder="Enter Slide Name"
                                      value={getFieldValue(
                                        slide,
                                        index,
                                        "name"
                                      )}
                                      autoFocus
                                      onChange={(event) =>
                                        slideMessageSubmit(
                                          event,
                                          "",
                                          " Slide Name",
                                          slide?.slideid?._id,
                                          index
                                        )
                                      }
                                      onKeyUp={(event) =>
                                        slideMessageSubmit(
                                          event,
                                          "enter",
                                          " Slide Name",
                                          slide?.slideid?._id,
                                          index
                                        )
                                      }
                                      onBlur={(event) =>
                                        slideMessageSubmit(
                                          event,
                                          "blur",
                                          " Slide Name",
                                          slide?.slideid?._id,
                                          index
                                        )
                                      }
                                      type="text"
                                    />
                                  ) : (
                                    <CustomTooltip
                                      title={getFieldValue(
                                        slide,
                                        index,
                                        "name"
                                      )}
                                      classes="slide-text-name"
                                    >
                                      <div style={{width: "112px", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                                       >
                                      <span className="value" style={{ padding: '2px' }}>
                                        {getFieldValue(slide, index, "name")}
                                      </span>
                                      </div>
                                    </CustomTooltip>
                                  )}
                                </div>
                                <div className="slide-actions">
                                  {/* <img
                                    src={EditIcon}
                                    alt="Edit"
                                    className="slide-action-icon edit-icon"
                                    onClick={(e) => handleEditSlide(slide, e)}
                                    title="Edit slide in editor"
                                  /> */}
                                  <img
                                    src={DeleteIconOne}
                                    alt="Delete"
                                    className="slide-action-icon delete-icon"
                                    onClick={(e) =>
                                      handleDeleteSlide(slide, index, e)
                                    }
                                    title="Delete slide"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>

        {/* Right Content Column: Stepper + (Canvas + RightPanel) */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {/* Stepper - Now inside the content area to the right of left panel */}
          <div className="pdf-editor-stepper-wrapper">
            <ModuleStepper
              currentStep={currentStep}
              steps={steps}
              className="pdf-flow-stepper"
              leftControls={
                <>
                  <CustomTooltip
                    title="Flow Creator"
                    classes="editor-wrapper-save-popover"
                  >
                    <img
                      src={FlowCreatorIcon}
                      alt="Flow Creator"
                      onClick={handleFlowCreator}
                      style={{
                        cursor: "pointer",
                        opacity: flowCreatorLoading ? 0.6 : 1,
                        width: "24px",
                        height: "24px",
                        flexShrink: 0,
                        objectFit: "contain",
                      }}
                    />
                  </CustomTooltip>
                  <CustomTooltip title="Select Area" className="editor-wrapper-save-popover">
                    <button
                      className={`tool-btn ${toolMode === "select" ? "active" : ""}`}
                      onClick={() => setToolMode("select")}
                      style={{marginLeft: "11px"}}
                    >
                      <img
                        src={toolMode === "select" ? EditIconActive : EditIcon}
                        alt="Select"
                      />
                      <span>Select Area</span>
                    </button>
                  </CustomTooltip>

                  <CustomTooltip title="Erase" className="editor-wrapper-save-popover">
                    <button
                      className={`tool-btn ${toolMode === "erase" ? "active" : ""}`}
                      onClick={() => setToolMode("erase")}
                      style={{marginLeft: "11px"}}
                    >
                      <img
                        src={toolMode === "erase" ? EraseIconActive : EraseIcon}
                        alt="Erase"
                      />
                      <span>Erase</span>
                    </button>
                  </CustomTooltip>
                </>
              }
            >
              <div className="preview-navigation">
                {/* Pagination arrows on the right */}
                <button
                  className="nav-arrow"
                  onClick={handlePrevSlide}
                  disabled={selectedSlideIndex === 0}
                >
                  <img
                    src={NewSlideLeftArrowIcon}
                    alt="Previous"
                    style={{ width: "10px", height: "10px" }}
                  />
                </button>
                <span className="page-counter">
                  {selectedSlideIndex + 1}/{slides.length}
                </span>
                <button
                  className="nav-arrow"
                  onClick={handleNextSlide}
                  disabled={selectedSlideIndex === slides.length - 1}
                >
                  <img
                    src={NewSlideRightArrowIcon}
                    alt="Next"
                    style={{ width: "10px", height: "10px" }}
                  />
                </button>
              </div>
            </ModuleStepper>
          </div>

          {/* Main Content Area (Canvas + Right Panel) */}
          <div
            className="editor-content-container pdf-editor-main"
            style={{
              flex: 1,
              borderTop: "1px solid #e3e3e3",
              minHeight: 0,
              display: "flex",
              flexDirection: "row",
              overflow: "hidden",
            }}
          >
            <div className="pdf-editor-canvas-container"
              style={{
                width: "calc(100vw - 560px)",
                height: "calc(100vh - 144px)",
                overflowX: `${(scalePercent > 100 || !showRightPanel || !isLeftPanelOpen ) ? "auto" : contentHeight > 768 ? "hidden" : "unset"}`,
                overflowY: `${scalePercent > 100 ? "auto" : "unset"}`,
                justifyContent: `${scalePercent > 100 ? "start" : "center"}`
              }}
            >
              {slideLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    height: "calc(100vh - 144px)",
                    fontSize: "14px",
                    color: "#666",
                  }}
                >
                  Loading slide content...
                </div>
              ) : slideHtml ? (
                <div
                  ref={htmlWrapperRef}
                  className="pdf-editor-html-wrapper"
                  style={{
                    position: "unset",
                    width: "auto",
                    maxWidth: "1024px",
                    minHeight: "768px",
                    height: "auto",
                    // Use contentScale to fit container, combined with zoom scalePercent
                    transform: `scale(${contentScale})`,
                    transformOrigin: `${scalePercent > 100 ? "top left" : "top center"}`, // Pivot from top for proper scrolling
                    borderRadius: "6px",
                    border: "1px solid #DDDFDD",
                    backgroundColor: "#fff",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    boxSizing: "border-box",
                    overflow: "auto", // Allow scrolling when content exceeds viewport
                    flexShrink: 0, // Prevent shrinking in flex container
                  }}
                >
                  <style dangerouslySetInnerHTML={{ __html: slideCss }} />
                  <style>{`
                    .pdf-editor-html-content {
                      width: 1024px !important;
                      max-width: 1024px !important;
                      min-height: 768px !important;
                      height: ${contentHeight+"px"} !important;
                      box-sizing: border-box !important;
                      overflow-x: hidden !important;
                      overflow-y: visible !important;
                      word-wrap: break-word !important;
                      position: relative !important;
                      margin: 0 !important;
                      padding: 0 !important;
                    }

                    .pdf-editor-html-content > *:not(.pdf-selected-area-link) {
                      max-width: 1024px !important;
                      box-sizing: border-box !important;
                    }
                    .pdf-editor-html-content img {
                      max-width: 1024px !important;
                      width: auto !important;
                      height: auto !important;
                      display: block !important;
                    }
                    .pdf-editor-html-content table {
                      max-width: 1024px !important;
                      width: 100% !important;
                      table-layout: auto !important;
                      word-wrap: break-word !important;
                    }
                    .pdf-editor-html-content body {
                      width: 100% !important;
                      min-height: 768px !important;
                      height: auto !important;
                      max-width: 1024px !important;
                      overflow-x: hidden !important;
                      overflow-y: visible !important;
                      margin: 0 !important;
                      padding: 0 !important;
                      box-sizing: border-box !important;
                    }
                    .pdf-editor-html-content div,
                    .pdf-editor-html-content p,
                    .pdf-editor-html-content span {
                      max-width: 1024px !important;
                      word-wrap: break-word !important;
                    }
                    /* Style for embedded selected areas - show dotted borders in editor */
                    .pdf-selected-area-link {
                      border: 1px dashed #54565B !important;
                      border-style: dashed !important;
                      border-width: 1px !important;
                      border-color: #54565B !important;
                      background-color: rgba(84, 86, 91, 0.05) !important;
                      box-sizing: border-box !important;
                      display: block !important;
                      text-decoration: none !important;
                    }
                    .pdf-selected-area-link[href="#"] {
                      pointer-events: auto !important;
                    }
                  `}</style>
                  {/* Use sanitized HTML div instead of iframe */}
                  <div
                    ref={htmlContentRef}
                    className="pdf-editor-html-content"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(slideHtml, {
                        ADD_TAGS: ["style"], // Allow style tags if needed
                        ADD_ATTR: [
                          "target",
                          "class",
                          "style",
                          "id",
                          "data-slide-id",
                          "href",
                        ], // Allow specific attributes
                        WHOLE_DOCUMENT: true, // Keep head/body if present (though we are injecting into div)
                      }),
                    }}
                    style={{
                      position: "relative",
                      width: "100%",
                      maxWidth: "1024px",
                      minHeight: "768px",
                      height: "auto",
                      boxSizing: "border-box",
                      overflowX: "hidden",
                      overflowY: "visible",
                      wordWrap: "break-word",
                    }}
                  />
                  {/* Removed iframe block */}
                  {/*
                  {iframeSrc ? (
                    <iframe
                      // ...
                    />
                  ) : (
                    // ... (old div fallback)
                  )}
                  */}

                  {/* Drawing overlay for selection - full area selectable */}
                  {toolMode === "select" && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: `${contentHeight}px`,
                        minHeight: "768px",
                        cursor: "crosshair",
                        zIndex: 2000,
                        pointerEvents: "auto",
                      }}
                      onMouseDown={(e) => {
                        // Get exact click position first, then handle mouse down
                        handleHtmlMouseDown(e);
                      }}
                      onMouseMove={handleHtmlMouseMove}
                      onMouseUp={handleHtmlMouseUp}
                      onMouseLeave={handleHtmlMouseUp}
                      onWheel={(e) => {
                        // Always allow scrolling - pass wheel events to wrapper
                        if (htmlWrapperRef.current) {
                          // e.preventDefault();
                          e.stopPropagation();
                          htmlWrapperRef.current.scrollTop += e.deltaY;
                        }
                      }}
                    >
                      {/* Current selection rectangle being drawn */}
                      {selectionRef.current && (
                        <div
                          style={{
                            position: "absolute",
                            left: `${
                              selectionRef.current.width < 0
                                ? selectionRef.current.x +
                                  selectionRef.current.width
                                : selectionRef.current.x
                            }px`,
                            top: `${
                              selectionRef.current.height < 0
                                ? selectionRef.current.y +
                                  selectionRef.current.height
                                : selectionRef.current.y
                            }px`,
                            width: `${Math.abs(selectionRef.current.width)}px`,
                            height: `${Math.abs(
                              selectionRef.current.height
                            )}px`,
                            border: "1px dashed #54565B",
                            borderStyle: "dashed",
                            borderWidth: "1px",
                            borderColor: "#54565B",
                            backgroundColor: "rgba(84, 86, 91, 0.05)",
                            pointerEvents: "none",
                            boxSizing: "border-box",
                          }}
                        />
                      )}
                    </div>
                  )}
                  {/* Overlay for selected areas - also handles erase */}
                  {toolMode === "erase" && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: `${contentHeight}px`,
                        minHeight: "768px",
                        cursor: `url(${CursorEraserIcon}) 12 12, auto`,
                        zIndex: 2000,
                        pointerEvents: "auto",
                      }}
                      onClick={(e) => {
                        const { x, y } = getHtmlCoordinates(e);
                        eraseSelectedArea(x, y);
                      }}
                    />
                  )}
                  {/* Overlay for selected areas - only show when NOT in select mode and areas are not yet linked */}
                  {/* Since areas are embedded in HTML, we don't need overlay overlays - they're already visible as <a> tags */}
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    fontSize: "14px",
                    color: "#999",
                  }}
                >
                  No content available for this slide
                </div>
              )}
              {/* Keep hidden canvas for potential future use */}
              {imageUrl && (
                <>
                  <img
                    ref={imageRef}
                    src={imageUrl}
                    alt="PDF Slide"
                    onLoad={handleImageLoad}
                    style={{ display: "none" }}
                  />
                  <canvas ref={canvasRef} style={{ display: "none" }} />
                </>
              )}
            </div>

            {/* Right Sidebar for Link Selection (Placeholder based on design images if needed, or just layout spacer) */}

            {/* Selected Areas List - keeping hidden or movable */}
            {selectedAreas.length > 0 && (
              <div
                className="selected-areas-panel"
                style={{
                  position: "absolute",
                  bottom: "20px",
                  right: "20px",
                  display: "none",
                }}
              >
                {/* Keeping hidden for now as it's not in the main visual request, or maybe show it */}
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - moved to top level */}
        <div
          className="link-selection-panel"
          style={{ flex: `0 0 ${showRightPanel ? "250px" : "0px"}` }}
        >
          <div
            style={{
              position: "absolute",
              width: "14px",
              height: "58px",
              top: `${window.innerHeight / 2 - 30}px`,
              translate: "-14px",
              zIndex: "1",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderTop: "solid 1px #e6e6eb",
              borderBottom: "solid 1px #e6e6eb",
              borderLeft: "solid 1px #e6e6eb",
              borderRight: "1px solid #fff",
              borderTopLeftRadius: "6px",
              borderBottomLeftRadius: "6px",
              cursor: "pointer",
            }}
            onClick={() => setShowRightPanel(!showRightPanel)}
          >
            <img
              src={
                showRightPanel ? NewSlideRightArrowIcon : NewSlideLeftArrowIcon
              }
              alt=""
              width={19}
              height={19}
              className="expand-right"
            />
          </div>
          {showRightPanel && (
            <>
              <div className="link-selection-header">
                <h4>Link selection to</h4>
              </div>
              <div className="link-selection-content">
                <div>
                  <span>Slide</span>
                  <div
                    className="custom-dropdown-container"
                    ref={dropdownRef}
                    style={{ position: "relative" }}
                  >
                    <div
                      className={`custom-dropdown-trigger ${
                        !selectedAreaForLink ? "disabled" : ""
                      }`}
                      onClick={() =>
                        selectedAreaForLink &&
                        setIsDropdownOpen(!isDropdownOpen)
                      }
                    >
                      <span className="selected-value">
                        {(() => {
                          if (!selectedAreaForLink) return "Select Link";
                          if (!linkSlideId) return "Select Link";
                          const selectedSlideObj = slides.find(
                            (s) => s.slideid?._id === linkSlideId
                          );
                          if (selectedSlideObj) {
                            const index = slides.findIndex(
                              (s) => s.slideid?._id === linkSlideId
                            );
                            return `${index + 1}. ${
                              selectedSlideObj.slideid?.name ||
                              `Slide ${index + 1}`
                            }`;
                          }
                          return "Select Link";
                        })()}
                      </span>
                      <img
                        src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E"
                        alt="arrow"
                        className={`dropdown-arrow ${
                          isDropdownOpen ? "open" : ""
                        }`}
                      />
                    </div>

                    {isDropdownOpen && selectedAreaForLink && (
                      <div className="custom-dropdown-options">
                        {slides
                          .filter(
                            (slide) =>
                              slide.slideid?._id !== selectedSlide?.slideid?._id
                          )
                          .map((slide, idx) => {
                            // Correct index calculation relative to full list
                            const actualIndex = slides.findIndex(
                              (s) => s.slideid?._id === slide.slideid?._id
                            );
                            return (
                              <CustomTooltip
                                title={`${actualIndex + 1}.${" "}${
                                  slide.slideid?.name ||
                                  `Slide ${actualIndex + 1}`
                                }`}
                                placement="left"
                              >
                                <div
                                  key={slide.slideid?._id || idx}
                                  className={`custom-option ${
                                    linkSlideId === slide.slideid?._id
                                      ? "selected"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    setLinkSlideId(slide.slideid?._id);
                                    setLinkType("slide");
                                    setLinkUrl("");
                                    setIsDropdownOpen(false);
                                    const updatedSelectedAreas =
                                      selectedAreas.map((area) => {
                                        if (area.id === selectedAreaForLink) {
                                          return {
                                            ...area,
                                            linkType: "slide",
                                            linkUrl: "",
                                            linkSlideId: slide.slideid?._id,
                                          };
                                        } else {
                                          return area;
                                        }
                                      });

                                    setSelectedAreas(updatedSelectedAreas);

                                    if (baseHtmlRef.current) {
                                      const updatedHtml =
                                        modifyHtmlWithSelectedAreas(
                                          baseHtmlRef.current,
                                          updatedSelectedAreas
                                        );
                                      const container =
                                        document.getElementById("wrapper");
                                      container.innerHTML = updatedHtml;
                                      setSlideHtml(updatedHtml);
                                      attachSlide();
                                    }
                                  }}
                                >
                                  {actualIndex + 1}.{" "}
                                  {slide.slideid?.name ||
                                    `Slide ${actualIndex + 1}`}
                                </div>
                              </CustomTooltip>
                            );
                          })}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <span>URL</span>
                  <input
                    type="text"
                    value={selectedAreaForLink ? linkUrl : ""}
                    onChange={(e) => {
                      if (selectedAreaForLink) {
                        setLinkUrl(e.target.value);
                        setLinkType("url");
                        setLinkSlideId("");
                        // Auto-save link when URL is entered - preserve ALL existing areas
                        setSelectedAreas((prevAreas) => {
                          // Ensure we're working with the latest state
                          return prevAreas.map(
                            (area) =>
                              area.id === selectedAreaForLink
                                ? {
                                    ...area,
                                    linkType: "url",
                                    linkUrl: e.target.value,
                                    linkSlideId: "",
                                  }
                                : area // Preserve all other areas unchanged
                          );
                        });
                      }
                    }}
                    disabled={!selectedAreaForLink}
                    placeholder="Add URL"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Flow Creator - Show All Slides */}
      {showAllSlides && (
        <SlideSwapperLeftPanel
          editor={null}
          setState={() => {}}
          showAllSlides={showAllSlides}
          showSlidesPanel={setShowAllSlides}
          appData={appData}
          setShowAllSlides={setShowAllSlides}
          getVeevaLinkedmodules={() => {}}
          setDeckData={setDeckData}
          setSlideID={(newSlides) => {
            const slideIndex = newSlides.findIndex(
              (slide) => slide?.slideid?._id === selectedSlide?.slideid?._id
            );
            if (slideIndex !== selectedSlideIndex) {
              setSelectedSlideIndex(slideIndex);
            }
            setSlides(newSlides);
            if (deckData) {
              setDeckData({ ...deckData, slides: newSlides });
            }
          }}
          setCurrentSlide={(slide) => {
            if (slide && slide.master) {
              const index = slides.findIndex(
                (s) => s.slideid?._id === slide.master?.slideid?._id
              );
              if (index !== -1) {
                handleSlideClick(slides[index], index);
              }
            }
          }}
          selectedCardHandler={(data, currentSlide, slideIndex) => {
            if (typeof currentSlide === "string") {
              const index = slides.findIndex(
                (s) => s.slideid?._id === currentSlide
              );
              if (index !== -1) {
                handleSlideClick(slides[index], index);
              }
            }
          }}
          swapped={false}
          setSlideCount={() => {}}
          setSwapped={() => {}}
          slideID={slides}
          setSaveLoading={setSaveLoading}
          setFlowCreatorLoading={setFlowCreatorLoading}
          deckData={deckData}
          handleSave={handleSave}
          isPdfFlow={true}
        />
      )}

      {/* Preview Modal */}
      {showPreview && (
        <PreviewModal
          showPreview={showPreview}
          setShowPreView={setShowPreView}
          setLoading={setPreviewLoading}
          deckData={deckData}
          slideID={slides.map((s, idx) => ({ ...s, index: idx + 1 }))}
          currentSlide={{ master: selectedSlide, child: {} }}
          lastChild={() => selectedSlideIndex === slides.length - 1}
          inParent={() => selectedSlideIndex === 0}
          appData={appData}
          previewLoading={previewLoading}
          previewUrlReady={previewUrlReady}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletePrompt && (
        <SlideDeleteValidator
          prompt={deletePrompt}
          setPrompt={setDeletePrompt}
          deleteSlide={confirmDeleteSlide}
          nthSlide={deleteSlideIndex !== null ? deleteSlideIndex + 1 : 1}
          child={false}
          isPdf={true}
        />
      )}

      {/* Metadata Modal */}
      {showMetadataModal && (
        <>
          {metadataLoading && (
            <CircularProgressBar isloading={metadataLoading} />
          )}
          <Modal
            show={showMetadataModal}
            onHide={closeMetadataModal}
            dialogClassName="clone-template-forms"
          >
            <Modal.Body className="clone-template-forms-modal">
              <div className="d-flex create-template-main">
                <section className="create-template-left-section">
                  <div className="create-template-left-section-content">
                    <h2 className="create-template-left-section-header">
                      METADATA INFORMATION
                    </h2>
                    <div
                      className="create-template-source"
                      style={{ marginTop: 0 }}
                    ></div>
                    <div className="create-template-created-by">
                      <h3 className="create-template-created-by-name">
                        Created by
                      </h3>
                      <p className="create-template-created-by-content">
                        {traceabilityData?.created?.name || userName || "-"}
                      </p>
                    </div>
                    <div className="create-template-created-on">
                      <h3 className="create-template-created-on-name">
                        Created on
                      </h3>
                      <p className="create-template-created-on-content">
                        {traceabilityData?.created_at
                          ? convertInDate(traceabilityData.created_at, "normal")
                          : convertInDate(Date(), "normal")}
                      </p>
                    </div>
                    {traceabilityData?.veevaMetadata[0]?.docNumber && (
                      <div className="create-template-created-on">
                        <h3 className="create-template-created-on-name">
                          Promomats PDF Doc ID
                        </h3>
                        <p className="create-template-created-on-content">
                          {traceabilityData?.veevaMetadata[0]?.docNumber}
                        </p>
                      </div>
                    )}
                    <div className="create-template-created-on">
                      <h3 className="create-template-created-on-name">
                        Product
                      </h3>
                      <p className="create-template-created-on-content">
                        {traceabilityData?.brand?.name ||
                          deckData?.brand?.name ||
                          "-"}
                      </p>
                    </div>
                    <div className="create-template-created-on">
                      <h3 className="create-template-created-on-name">
                        Country
                      </h3>
                      <p className="create-template-created-on-content">
                        {traceabilityData?.market?.name ||
                          deckData?.market?.name ||
                          "-"}
                      </p>
                    </div>
                  </div>
                </section>
                <section className="create-template-right-section">
                  <div className="create-template-right-section-content">
                    <div>
                      <TextField
                        className="create-template-input-field clone-template-field"
                        size="small"
                        variant="outlined"
                        label="e-Detail Name"
                        name="name"
                        fullWidth={true}
                        value={metadataName}
                        onChange={handleMetadataNameChange}
                        required
                        autoComplete="off"
                        style={{
                          fontSize: "12px",
                          transformOrigin: "top",
                          color: "#54565B",
                          marginTop: "4px",
                        }}
                        helperText={metadataHelperText}
                        error={metadataErrorMsg}
                        onBlur={() => {
                          if (
                            metadataName.trim().length >= 3 &&
                            !metadataErrorMsg
                          ) {
                            validateMetadataName();
                          }
                        }}
                      />
                    </div>
                  </div>
                </section>
              </div>
            </Modal.Body>
            <Modal.Footer className="clone-template-footer-section">
              <Button
                className="cancel-clone-template"
                type="button"
                onClick={closeMetadataModal}
              >
                CANCEL
              </Button>
              <Button
                className={`create-template-next-btn ${
                  metadataName.trim().length < 3 ||
                  metadataName.trim().length > 30 ||
                  metadataErrorMsg
                    ? "disabled-template-btn"
                    : "enabled-template-btn"
                }`}
                type="button"
                onClick={handleMetadataPublish}
                disabled={
                  metadataName.trim().length < 3 ||
                  metadataName.trim().length > 30 ||
                  metadataErrorMsg ||
                  metadataLoading
                }
              >
                PUBLISH HTML
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </div>
  );
};

export default PdfEditor;
